'use client';

import { postHelpZendesk } from '@/api/product';
import PresTypeDialog from '@/app/lens-process/components/steps/PrescriptionType/components/PresTypeDialog';
import Input from '@/components/Form/FormInput';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import PopupResponsive from '@/components/PopupResponsive';
import { toast } from '@/components/Toast';
import Upload from '@/components/Upload';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styles from './LeaveMsg.module.scss';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';

interface LeaveMsgProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  email: string;
  phone: string;
  prescription: string;
  images: Array<{ type: string; url: string }>;
}

const LeaveMsg = ({ open, onClose }: LeaveMsgProps) => {
  const t = useTranslations('lens-process');
  const isWap = useMediaQuery('(max-width:1280px)');
  const userInfo = useStore((state) => state.userInfo);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [presDialog, setPresDialog] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<Array<{ type: string; url: string }>>([]);
  const { deviceType } = useContext(ResponsiveContext);
  const isMobile = deviceType === EPlatform.WAP;

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      email: userInfo?.email || '',
      phone: '',
      prescription: '',
      images: [],
    },
  });

  // 重置表单
  const resetForm = () => {
    reset({
      email: userInfo?.email || '',
      phone: '',
      prescription: '',
      images: [],
    });
    setUploadedImages([]);
  };

  // 处理文件上传
  const handleFileUpdate = (fileInfo: Array<{ type: string; s3Url: string }>) => {
    logger.info('Upload--handleFileUpdate-1', fileInfo);

    // 更新本地状态
    const newImages = [...fileInfo.map((item) => ({ type: item.type, url: item.s3Url }))];

    setUploadedImages(newImages);

    // 直接更新表单值
    setValue('images', newImages, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    logger.info('Upload--handleFileUpdate-2', newImages);
  };

  const scrollToError = () => {
    setTimeout(() => {
      const errorElement = document.querySelector('.leave-msg-error-message');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 提交表单
  const onSubmit = async (data: FormData) => {
    try {
      logger.info('Upload--Form--onSubmit-1', data);
      setSubmitLoading(true);
      const res = await postHelpZendesk(data);
      if (res.code === 200) {
        toast.success(t('submitSuccessfully'));
        onClose();
        resetForm();
      }
    } catch (error) {
      logger.error('Failed to submit prescription help request:', error);
      toast.error(t('submitFailed') || 'Submit failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 添加调试函数，显示表单错误
  const handleFormSubmit = () => {
    logger.info('Form submission attempted');
    logger.info('Current form errors:', errors);
    logger.info('Current images value:', getValues('images'));
    logger.info('Current uploadedImages state:', uploadedImages);

    // 确保表单中的images值是最新的
    if (uploadedImages.length > 0 && getValues('images').length === 0) {
      setValue('images', uploadedImages, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    handleSubmit(
      (data) => {
        logger.info('Form is valid, submitting with data:', data);
        onSubmit(data);
      },
      (err) => {
        logger.info('Form validation failed:', err);
        scrollToError();
      },
    )();
  };

  // 处理关闭
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <PopupResponsive
      open={open}
      position="bottom"
      showDivider={false}
      closeOnClickOverlay={false}
      title={t('leaveAMessage')}
      bodyClassName="!p-0 !max-h-[70vh] overflow-auto"
      hasFooter
      okText={t('submitReview')}
      onClose={handleClose}
      onOk={handleFormSubmit}
      loading={submitLoading}
    >
      <div className={`relative ${isMobile ? styles.mobile : ''}`}>
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-auto px-[14px] xl:px-[20px]">
            <form className="">
              <div className="xl:flex xl:gap-[34px]">
                <div className="mb-[15px] flex-1">
                  <label className="text-[14px] font-bs">
                    {t('addYourEmail')} <span className="text-[#bc1b1b]">*</span>
                  </label>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: t('emailIsRequired'),
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: t('pleaseEnterAValidEmailAddress'),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          size="small"
                          key={field.name}
                          className="mt-[13px] mb-[28px] rounded-[6px] p-[10px]"
                          error={!!error}
                          helperText={error?.message}
                          slotProps={{
                            formHelperText: {
                              className: 'leave-msg-error-message',
                            },
                          }}
                        />
                      </>
                    )}
                  />
                </div>

                <div className="mb-[15px] flex-1">
                  <label className="text-[14px] font-bs">{t('phoneNumber')}</label>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^[\d+()-]+$/,
                        message: t('pleaseEnterAValidPhoneNumber'),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          key={field.name}
                          className="mt-[13px] mb-[28px] rounded-[6px] p-[10px]"
                          error={!!error}
                          helperText={error?.message}
                          slotProps={{
                            formHelperText: {
                              className: 'leave-msg-error-message',
                            },
                          }}
                        />
                      </>
                    )}
                  />
                </div>
              </div>

              <div className="mb-[15px]">
                <div className="flex justify-between xl:justify-start items-center">
                  <label className="text-[14px] font-bs">
                    {t('prescriptionType')} <span className="text-[#bc1b1b]">*</span>
                  </label>
                  <QuestionResponsive
                    className="f16 text-[#666666] cursor-pointer ml-[6px]"
                    onClick={() => setPresDialog(true)}
                  />
                </div>
                <Controller
                  name="prescription"
                  control={control}
                  rules={{
                    required: t('prescriptionTypeIsRequired'),
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        multiline
                        key={field.name}
                        rows={5}
                        placeholder={t('pleaseKindlyDescribeThePrescriptionYouNeed')}
                        className="mt-[13px] mb-[28px] rounded-[6px] p-[10px] h-[auto]"
                        slotProps={{
                          input: {
                            className: 'h-[auto]',
                          },
                          formHelperText: {
                            className: 'leave-msg-error-message',
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 'auto',
                            paddingLeft: 0,
                            paddingRight: 0,
                            fontSize: '14px',
                          },
                          '& textarea::placeholder': {
                            fontSize: isWap ? '12px' : '14px',
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </>
                  )}
                />
              </div>

              <div className="mb-[15px]">
                <label className="text-[14px] font-bs">
                  {t('uploadYourPrescription')} <span className="text-[#bc1b1b]">*</span>
                </label>
                <div className="mt-[13px] mb-[28px]">
                  <Controller
                    name="images"
                    control={control}
                    rules={{
                      required: t('pleaseUploadYourPrescription'),
                      validate: (value) => {
                        logger.info('Validating images:', value);
                        // 如果本地状态有图片但表单值没有，使用本地状态
                        if ((!value || value.length === 0) && uploadedImages.length > 0) {
                          return true;
                        }
                        return (value && value.length > 0) || t('pleaseUploadYourPrescription');
                      },
                    }}
                    render={({ fieldState: { error } }) => (
                      <>
                        <Upload
                          sceneId={2} // 2:处方图
                          multiple
                          maxCount={3}
                          maxSize={10}
                          imgAccept=".jpg,.jpeg,.png"
                          showImage
                          onChangeInfo={(fileInfo) => {
                            logger.info('Upload--fileInfo-0', fileInfo);
                            handleFileUpdate(fileInfo);
                          }}
                        />
                        {error && (
                          <p className="text-[12px] text-[#bc1b1b] mt-[8px] leave-msg-error-message">
                            {error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
            </form>
          </div>
          {/* 这里的按钮已经做到了组件里面 */}
        </div>
        {/* 处方类型说明弹窗 */}
        <PresTypeDialog open={presDialog} onClose={() => setPresDialog(false)} />
      </div>
    </PopupResponsive>
  );
};

export default LeaveMsg;
